import DataGrid from 'react-data-grid'
import type { SortColumn } from 'react-data-grid'
import { 
  useState,
  useMemo,
  useEffect
} from 'react'
import { AiOutlineUserAdd, AiFillCheckCircle } from 'react-icons/ai'
import { GiCancel } from 'react-icons/gi'
import { MdSupervisedUserCircle } from 'react-icons/md'
import { SiMicrosoftoutlook } from 'react-icons/si'
import { Tab } from '@headlessui/react'
import { 
  useForm,
  SubmitHandler
} from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import Select from 'react-select';

import Modal from '../../components/Modal'
import SelectOption from "../../components/SelectOption"
import SpinnerLoading from '../../components/SpinnerLoading'
import { SummaryRole } from '../../components/types'

import { UserColumns, getUserComparator } from '../../constants/Column'
import { DepartmentRoles } from '../../constants/Enum'
import { SubmitState } from '../../constants/EnumType'
import useUsers from '../../hooks/useUsers'
import { 
  Department,
  DgtPdIAMUser,
  Division
} from '../../models'
import { ProductionLine } from '../../models'
import { newUser } from '../../apis'
import { PageProps } from '../../components/types'

const SubmitLoading = () => {
  return <div className="flex flex-col items-center justify-center gap-5">
    <SpinnerLoading />
    <h5>กำลังสร้าง User</h5>
  </div>
}

export default function Users({ division: userDiv, line: userLine, department } : PageProps) {
  const [ sortColumns, setSortColumns ] = useState<readonly SortColumn[ ]>([ ]);
  const [ division, setDivision ] = useState<string>(userDiv || '')
  const [ line, setLine ] = useState<string>(userLine || '')
  const [ roleIdx, setRoleIdx ] = useState<number>(0)
  const [ create, setCreate ] = useState<boolean>(false)
  const [ refresh, setRefresh ] = useState<boolean>(false)
  const [ submitState, setSubmitState ] = useState<SubmitState>(SubmitState.INIT)
  const [ selectedRoles, setSelectedRoles ] = useState<{ value: string, label: string}[]>([])
  const [ errorFromApi, setErrorFromApi ] = useState<string>()

  const users = useUsers(department as Department, refresh, division, line)

  const roles = DepartmentRoles[department as Department]

  const summaryRoles = useMemo((): readonly SummaryRole[] => {
    return roles.map(role => {
      const count = users.filter(usr => usr.roles?.includes(role)).length
      return { role, count }
    })
  }, [ users, roles ]);

  const sortedRows = useMemo((): readonly DgtPdIAMUser[] => {
    if (sortColumns.length === 0) return users.filter(usr => usr.roles?.includes(roles[roleIdx]));

    return [...users].filter(usr => usr.roles?.includes(roles[roleIdx])).sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getUserComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [users, roles, sortColumns, roleIdx]);

  const schema = yup.object({
    employeeNo: yup.string().length(6, 'กรอกรหัสพนักงาน 6 หลัก'),
    firstName: yup.string().required('กรอกชื่อจริง'),
    lastName: yup.string().required('กรอกนามสุกล'),
    roles: yup.string().required('เลือกอย่างน้อย 1 Role'),
    email: yup.string().email('กรอกอีเมล์ไม่ถูกต้อง')
  })

  const { 
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues
  } = useForm<DgtPdIAMUser>({
    defaultValues: {
      department: department as Department,
      roles: undefined
    },
    resolver: yupResolver(schema)
  })

  const onCloseModal = () => {
    reset()
    setSelectedRoles([])
    setSubmitState(SubmitState.INIT)
    setCreate(false)
  }

  useEffect(() => {
    const arrayRoles = selectedRoles.map(role => (
      { name: role.value.replace('-ro', ''), readOnly: role.value.includes('-ro') }
    ))
    setValue('roles', arrayRoles.length > 0 ? JSON.stringify(arrayRoles) : undefined)
  }, [ selectedRoles, setValue ])

  const onSubmitForm: SubmitHandler<DgtPdIAMUser> = async (data) => {
    setSubmitState(SubmitState.SUBMITTING)
    const isSSO = (data.email && (data.email?.includes('@neo-corporate.com') || data.email?.includes('@neo-factory.biz'))) || false
    try {
      const suffixSSO = process.env.REACT_APP_ENV === 'PRD' ? 'prd' : `idp-${process.env.REACT_APP_ENV?.toLowerCase()}`
      await newUser({
        ...data, 
        username: isSSO ? `digital-production-${suffixSSO}_${data.email?.toLocaleLowerCase()}` : data.employeeNo!
      }, isSSO)
      setSubmitState(SubmitState.COMPLETED)
      setRefresh(!refresh)
    } catch (error) {
      console.error(error)
      setErrorFromApi('มี User นี้อยู่แล้ว (ConditionalCheckFailedException)')
      setSubmitState(SubmitState.FAILED)
    }
  }

  const onRowsChange = () => {
    setRefresh(!refresh)
  }

  const rolesWithReadOnly = [
    ...roles.map(role => ({ label: role, value: role, isDisabled: selectedRoles.map(r => r.value).includes(`${role}-ro`) })),
    ...roles.map(role => ({ label: `${role} - Read Only`, value: `${role}-ro`, isDisabled: selectedRoles.map(r => r.value).includes(role) }))
  ]

  return <>
    <div className='flex items-center justify-between p-3 px-4 border-b border-gray-200 bg-amber-100'>
      <h4 className='font-semibold '>รายชื่อผู้ใช้งานแผนก : {department}</h4>
      <button className='btn-success' onClick={() => setCreate(true)}><AiOutlineUserAdd className='mr-1 ' />New user</button>
    </div>
    <div className='flex items-center justify-between pr-4'>
      <Tab.Group
        onChange={setRoleIdx}
        >
        <Tab.List className='flex font-medium text-center text-gray-500 border-b border-gray-200 dark:text-gray-400 dark:border-gray-700'>
          {summaryRoles.map(role => 
            <Tab
              key={role.role}
              className={({ selected }) =>
                `${selected ? 'text-amber-500 border-b-2 border-amber-500':'text-gray-400'} p-3`
              }
            >{role.role}<span className='p-1 ml-2 text-xs font-bold rounded-full text-amber-700 bg-amber-400'>{role.count}</span></Tab>
          )}
        </Tab.List>
      </Tab.Group>
      <div className='flex gap-1'>
        <SelectOption title=''
          className=" w-fit justify-self-end"
          optionEnum={['', ...Object.values(Division)]}
          value={division}
          onChanged={val => setDivision(val)}
        />
        <SelectOption title=''
          className=" w-fit justify-self-end"
          optionEnum={['', ...Object.values(ProductionLine)]}
          value={line}
          onChanged={val => setLine(val)}
        />
      </div>
    </div>
    <DataGrid
      style={{ height: '80vh' }}
      columns={UserColumns}
      rows={sortedRows}
      defaultColumnOptions={{
        sortable: true,
        resizable: true
      }}
      sortColumns={sortColumns}
      onSortColumnsChange={setSortColumns}
      onRowsChange={onRowsChange}
    />
    <div className='flex gap-1 p-2 mb-5'>
      <MdSupervisedUserCircle size={20} className='text-red-500'/>
      <small className='italic text-muted'>จัดการ User ได้</small>
      <SiMicrosoftoutlook size={20} className='ml-3 text-blue-500 '/>
      <small className='italic text-muted'>เข้าระบบด้วย Email บริษัท (ไม่มีรหัสผ่าน)</small>
    </div>
    <Modal 
      show={create}
      onHide={() => setCreate(false)}
      title={`สร้าง User ใหม่ - ${department}`}
      maxWidth='max-w-xl'
    >
      <>
      {submitState === SubmitState.INIT ? <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className='grid grid-cols-2 gap-3 mb-3'>
          <div>
            <label htmlFor='employeeNo' className="after:content-['*'] after:ml-0.5 after:text-red-500 inline-block mb-2 text-gray-700 form-label">รหัสพนักงาน</label>
            <input
              type='text'
              className=" input-form-text"
              id='employeeNo'
              placeholder='กรอกรหัสพนักงาน 6 หลัก'
              {...register('employeeNo')}
            />
            {errors && errors.employeeNo ? <small className="error-label">{errors.employeeNo.message}</small> : null}
          </div>
          <div>
            <label htmlFor='email' className="inline-block mb-2 text-gray-700 form-label">อีเมล์</label>
            <input
              type='email'
              className=" input-form-text"
              id='email'
              placeholder='กรอกอีเมล์ (ถ้ามี)'
              {...register('email')}
            />
            {errors && errors.email ? <small className="error-label">{errors.email.message}</small> : null}
          </div>
          <div>
            <label htmlFor='firstName' className="inline-block mb-2 text-gray-700 after:content-['*'] after:ml-0.5 after:text-red-500 form-label">ชื่อ</label>
            <input
              type='text'
              className=" input-form-text"
              id='firstName'
              placeholder='ชื่อ'
              {...register('firstName')}
            />
            {errors && errors.firstName ? <small className="error-label">{errors.firstName.message}</small> : null}
          </div>
          <div>
            <label htmlFor='lastName' className="inline-block mb-2 text-gray-700 after:content-['*'] after:ml-0.5 after:text-red-500 form-label">สกุล</label>
            <input
              type='text'
              className=" input-form-text"
              id='lastName'
              placeholder='สกุล'
              {...register('lastName')}
            />
            {errors && errors.lastName ? <small className="error-label">{errors.lastName.message}</small> : null}
          </div>
          <div className='col-span-2 '>
            <label htmlFor='role' className="inline-block mb-2 text-gray-700 after:content-['*'] after:ml-0.5 after:text-red-500 form-label">Role</label>
            <Select
              isMulti
              name="roles"
              placeholder="เลือกอย่างน้อย 1 Role"
              menuPortalTarget={document.body}
              styles={{ 
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                multiValueLabel: (styles, { data }) => ({
                  ...styles,
                  color: data.value.includes('-ro') ? 'gray' : 'green',
                })
              }}
              options={rolesWithReadOnly}
              className="basic-multi-select"
              classNamePrefix="select"
              value={selectedRoles}
              onChange={vals => setSelectedRoles(Array.from(vals))}
            />
            {errors && errors.roles && selectedRoles.length === 0 ? <small className="error-label">{errors.roles.message}</small> : null}
          </div>
          <div>
            <label htmlFor='division' className="inline-block mb-2 text-gray-700 form-label">Division</label>
            <div className="flex gap-2">
              {Object.values(Division).map(division => 
                <div key={division} className="flex form-check form-check-inline">
                  <input {...register('division')} title={division} className="form-check-input input-radio" type="radio"
                    name="division" id={`division_${division}`} value={division} />
                  <label className="inline-block text-gray-800 form-check-label" htmlFor={`division_${division}`}>{division}</label>
                </div>
              )}
            </div>
          </div>
          <div>
            <label htmlFor='line' className="inline-block mb-2 text-gray-700 form-label">Line ผลิต</label>
            <div className="flex gap-2">
              {Object.values(ProductionLine).map(pdLine => 
                <div key={pdLine} className="flex form-check form-check-inline">
                  <input {...register('line')} title={pdLine} className="form-check-input input-radio" type="radio"
                    name="line" id={`pdLine_${pdLine}`} value={pdLine} />
                  <label className="inline-block text-gray-800 form-check-label" htmlFor={`pdLine_${pdLine}`}>{pdLine}</label>
                </div>
              )}
            </div>
          </div>
          <div className='col-span-2 '>
            <label htmlFor='line' className="inline-block mb-2 text-gray-700 form-label">อื่นๆ</label>
            <div className="form-check">
              <input className="float-left w-4 h-4 mt-1 mr-2 align-top" type="checkbox" value="" id="isManageUsers"
                { ...register('isAdmin')} />
              <label className="inline-block text-gray-800 form-check-label" htmlFor="isManageUsers">
                สามารถจัดการ User ได้ (เพิ่ม/ลด/แก้ไข Role)
              </label>
            </div>
          </div>
        </div>
        <button type='submit' className="float-right ml-3 btn-success" >เพิ่ม</button>
        <button type='button' className="float-right text-base btn-outline-success" onClick={() => setCreate(false)}>
          ปิด
        </button>
      </form> : null}
      {submitState === SubmitState.SUBMITTING ? <SubmitLoading /> : null}
      {submitState === SubmitState.COMPLETED ? <div className='flex flex-col items-center justify-center gap-5 text-center'>
        <AiFillCheckCircle size={96} className='text-green-600' />
        <h1 className='font-bold '> สร้าง User เรียบร้อย</h1>
        {getValues('email') && (getValues('email')?.includes('@neo-corporate.com') || getValues('email')?.includes('@neo-factory.biz')) ? <>
          <p className='text-xl '>Email : <span className='font-bold '>{getValues('email')}</span></p>
          <p className='text-xl '>เข้าระบบด้วยการกด <span className='italic font-bold text-blue-600 '>Sign In with Outlook</span> ได้เลย</p>
          <small className=' text-muted'>เมื่อเข้าระบบด้วย Sign In with Outlook จะเข้าผ่าน Email บริษัท</small>
        </> :
        <>
          <p className='text-xl '>Username : <span className='font-bold '>{getValues('employeeNo')}</span></p>
          <p className='text-xl '>Password ชั่วคราว : <span className='font-bold '>neofactory</span></p>
          <small className=' text-muted'>ระบบจะให้ตั้งรหัสผ่านใหม่ 8 หลัก เมื่อเข้าระบบครั้งแรก</small>
        </>}
        <button type="button" className="btn-outline-primary" onClick={onCloseModal}>ปิด</button>
      </div> : null}
      {submitState === SubmitState.FAILED ? <div className='flex flex-col items-center justify-center gap-5 text-center'>
        <GiCancel size={96} className='text-red-600' />
        <p className='text-xl '>{errorFromApi}</p>
        <button type="button" className="btn-outline-primary" onClick={onCloseModal}>ปิด</button>
      </div> : null}
      </>
    </Modal>
  </>
}